import React, { useState } from 'react';
import SEO from '../components/SEO';
import SearchForm from '../components/SearchForm';

const TFN = '+1 (855) 895-8503';
const TEL = 'tel:+18558958503';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'New Flight Booking Help | Flyairlinebooking',
  url: 'https://flyairlinebooking.com/newbooking',
  description:
    'Get help booking a new flight on any airline. Call our 24/7 travel agents to search fares, compare airline policies, and complete your reservation.',
  publisher: {
    '@type': 'Organization',
    name: 'Flyairlinebooking',
    url: 'https://flyairlinebooking.com',
    telephone: TFN,
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Does Flyairlinebooking represent any specific airline?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Flyairlinebooking is an independent travel agency. We are not affiliated with, endorsed by, or acting as an agent of any airline. We help travelers search, compare, and book fares across many carriers.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I book a flight on any airline by calling?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Yes. Call our 24/7 support line at ${TFN} and one of our agents will search available fares across airlines and help you complete a new booking.`,
      },
    },
    {
      '@type': 'Question',
      name: 'Will I know the airline\'s fare rules before I book?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Every airline sets its own fare rules for changes, cancellations, seating, and baggage. Our agents review the operating airline\'s policy with you before you confirm a new booking.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are there fees for booking by phone?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Our service fees are listed on our Service Fees page and are separate from any airline fare or airline-imposed fee. Fees are always disclosed before you confirm payment.',
      },
    },
  ],
};

const STEPS = [
  { icon: '📞', title: 'Call Us', desc: `Dial ${TFN} anytime, 24/7. No hold music runs long — a real agent picks up.` },
  { icon: '🔍', title: 'We Search Fares', desc: 'We compare pricing across airlines for your route and dates to find you options that fit your budget.' },
  { icon: '📋', title: 'Review Airline Policy', desc: 'We walk you through the operating airline\'s fare rules — change, cancellation, baggage — before you decide.' },
  { icon: '✅', title: 'Confirm & Fly', desc: 'Once you approve the fare and policy, we complete the booking and email your confirmation.' },
];

const POLICY_TOPICS = [
  { icon: '🔄', title: 'Change & Cancellation Rules', desc: 'Every airline fare type (Basic Economy, Standard, Flexible/Refundable) carries its own change and cancellation terms set by that airline — not by us.' },
  { icon: '🧳', title: 'Baggage Allowances', desc: 'Checked and carry-on bag limits, weight allowances, and bag fees vary by airline and fare class. We confirm the exact allowance for your itinerary.' },
  { icon: '💺', title: 'Seat Selection', desc: 'Some airlines include seat selection in the fare; others charge separately. We tell you which applies before you pay.' },
  { icon: '⏱️', title: 'Same-Day Changes', desc: 'Many airlines offer same-day flight change or standby options, typically for a fee. We check availability and airline rules for your specific flight.' },
];

export default function NewBookingPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const FAQS = faqJsonLd.mainEntity.map(q => ({ q: q.name, a: q.acceptedAnswer.text }));

  return (
    <div className="bg-white">
      {/* Disclaimer */}
      <div className="bg-amber-50 border-b border-amber-100 py-2.5 px-4 text-center text-xs text-amber-800">
        <strong>Disclaimer:</strong> Flyairlinebooking is an independent travel agency and does not represent, own, or act on behalf of any airline. Airline names, logos, and trademarks belong to their respective owners.
      </div>

      <SEO
        title="New Flight Booking Help — Call to Book"
        canonical="/newbooking"
        description="Need to book a new flight? Call Flyairlinebooking 24/7 to search fares across airlines, review airline policies, and complete your booking with a live agent."
        keywords="new flight booking, book a flight, book flight by phone, airline booking help, flight reservation assistance"
        jsonLd={[jsonLd, faqJsonLd]}
      />

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-800 text-white overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-4 pt-12 pb-10 sm:pt-16 sm:pb-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-medium mb-4">
            🧭 Independent Booking Assistance — All Major Airlines
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
            Book a New Flight<br className="hidden sm:block" />
            <span className="text-emerald-300">Talk to a Live Agent, 24/7</span>
          </h1>
          <p className="text-emerald-100 text-base sm:text-lg mt-3 max-w-xl mx-auto">
            We search fares across airlines and explain each airline's policies before you book — no airline affiliation, just help finding your flight.
          </p>

          <div className="mt-7">
            <a
              href={TEL}
              className="inline-flex items-center justify-center gap-2 bg-white text-emerald-900 font-bold px-8 py-4 rounded-xl hover:bg-emerald-50 transition-colors text-lg shadow-lg"
            >
              📞 Call {TFN} to Book
            </a>
            <p className="text-emerald-200 text-xs mt-3">Available 24 hours a day, 7 days a week</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 sm:p-7 mt-8 text-left">
            <p className="text-white/70 text-sm font-medium mb-4">Or search flights online first</p>
            <SearchForm />
          </div>
        </div>
      </div>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">How New Booking Help Works</h2>
          <p className="text-gray-500">Four simple steps from call to confirmation.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white border border-gray-100 shadow-sm rounded-2xl p-5 relative">
              <span className="absolute top-4 right-4 text-xs font-bold text-emerald-300">{i + 1}</span>
              <div className="text-3xl mb-3">{s.icon}</div>
              <h3 className="font-bold text-gray-900 mb-1.5">{s.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Airline policy info */}
      <section className="bg-gray-50 py-12 sm:py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Know the Airline's Policy Before You Book</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Every airline sets its own rules. We're not the airline — we make sure you understand its policy before you commit to a fare.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {POLICY_TOPICS.map(t => (
              <div key={t.title} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex gap-4">
                <div className="text-3xl shrink-0">{t.icon}</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{t.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-400 mt-6 max-w-2xl mx-auto">
            Policy details vary by airline, route, and fare class and are set solely by the operating airline. Our agents confirm current terms for your specific itinerary at the time of booking.
          </p>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-gradient-to-r from-emerald-900 to-emerald-700 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">Ready to Book Your Flight?</h2>
          <p className="text-emerald-200 mb-7 text-base">
            Call now and a live agent will search fares, explain the airline's policy, and get you booked — no bots, no waiting on hold for hours.
          </p>
          <a
            href={TEL}
            className="inline-flex items-center justify-center gap-2 bg-white text-emerald-900 font-bold px-8 py-3.5 rounded-xl hover:bg-emerald-50 transition-colors"
          >
            📞 Call {TFN}
          </a>
          <p className="text-emerald-300 text-xs mt-5">Independent travel agency · Not affiliated with any airline</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full text-left px-5 py-4 flex items-start justify-between gap-3 hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-gray-800 text-sm leading-snug">{faq.q}</span>
                <span className={`text-emerald-500 font-bold text-lg shrink-0 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
              </button>
              {openFaq === i && (
                <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-50">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 py-10 text-center">
        <p className="text-gray-500 text-sm">
          Need help booking a flight?{' '}
          <a href={TEL} className="text-emerald-600 font-semibold hover:underline">{TFN}</a>
          {' '}— available 24/7. Flyairlinebooking is an independent travel agency and is not affiliated with any airline.
        </p>
      </section>
    </div>
  );
}
